/**
 * Small presentational indicator components shared by the tile and the detail drawer.
 *   - RsiGauge   : 0–100 bar with oversold / overbought zones and a value marker
 *   - Gate       : the RSI → ADX → EMA step sequence with pass/warn/fail styling
 *   - SignalPill : BUY / HOLD / SELL pill with score
 *   - Chg        : signed % with up/down colour
 *   - Sparkline  : tiny trend line from priceHistory (renders nothing if absent)
 */
import React from 'react';
import { GateStatus, GATE_LABELS, GATE_FULL, fmtPct } from '../utils/signal';

type Classification = 'BUY' | 'HOLD' | 'SELL' | 'NONE';

const KNOB: Record<GateStatus, string> = { pass: '✓', warn: '~', fail: '✕' };

export function RsiGauge({ value, height = 8, showZones = true }: {
  value: number | null;
  height?: number;
  showZones?: boolean;
}) {
  const v = value == null ? 0 : Math.max(1, Math.min(99, value));
  return (
    <div className="rsi-gauge" style={{ height }} title={value == null ? 'RSI —' : `RSI ${value.toFixed(1)}`}>
      {showZones && <><span className="rsi-zone os" /><span className="rsi-zone ob" /></>}
      <span className="rsi-fill" style={{ width: `${v}%` }} />
      {value != null && <span className="rsi-mark" style={{ left: `${v}%` }} />}
    </div>
  );
}

export function Gate({ gate, size = 'md' }: { gate: GateStatus[]; size?: 'sm' | 'md' }) {
  return (
    <div className={`gate gate-${size}`}>
      {gate.map((st, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="gate-arrow">→</span>}
          <span className={`gstep ${st}`} title={GATE_FULL[i]}>
            <span className="gknob">{KNOB[st]}</span>{GATE_LABELS[i]}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

export function SignalPill({ classification, score, size = 'md' }: {
  classification: Classification;
  score: number;
  size?: 'sm' | 'md';
}) {
  const cls = classification.toLowerCase();
  return (
    <span className={`sig-pill ${cls} pill-${size}`}>
      <span className="sig-dot" />{classification}<span className="sig-score">{score}</span>
    </span>
  );
}

export function Chg({ value, arrow = true }: { value: number | null; arrow?: boolean }) {
  if (value == null) return <span className="chg muted">—</span>;
  const up = value >= 0;
  return (
    <span className={`chg ${up ? 'up' : 'dn'}`}>
      {arrow ? (up ? '▲ ' : '▼ ') : ''}{fmtPct(value)}
    </span>
  );
}

/** Maps a classification to its line colour using the app's signal palette. */
export function signalColor(classification: Classification): string {
  switch (classification) {
    case 'BUY':  return 'var(--success)';
    case 'HOLD': return '#60a5fa';
    case 'SELL': return '#f97316';
    default:     return 'var(--text-muted)';
  }
}

export function Sparkline({ values, color, height = 38, fill = true, dot = true }: {
  values?: number[] | null;
  color: string;
  height?: number;
  fill?: boolean;
  dot?: boolean;
}) {
  const gid = React.useId();
  if (!values || values.length < 2) return null;
  const w = 150, h = height, pad = 3;
  const min = Math.min(...values), max = Math.max(...values), rng = (max - min) || 1;
  const pts = values.map((v, i) => [
    pad + (i / (values.length - 1)) * (w - pad * 2),
    h - pad - ((v - min) / rng) * (h - pad * 2),
  ] as const);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L ${w - pad} ${h} L ${pad} ${h} Z`;
  const last = pts[pts.length - 1];
  return (
    <svg className="spark" width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
      style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      {fill && <path d={area} fill={`url(#${gid})`} />}
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round"
        strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {dot && <circle cx={last[0]} cy={last[1]} r={2.6} fill={color} />}
    </svg>
  );
}
